import { Currencies, PaymentMethods, GettingFile } from '@/enums';
import { ApiPagination, BaseResponse, RequestPagination } from '@/types';

export type ProductPriceField = {
  [key in Currencies]: number | null;
}

export type ProductPrice = {
  [key: string]: ProductPriceField;
}

interface Benefit {
  title: string,
  description: string,
}

export interface Characteristic {
  id: number,
  name: string,
  value: string
}

export interface ProductFile {
  id?: number,
  name: string,
  date_upload: string,
  type: GettingFile.Type,
}

export interface LicenseKeys {
  id: number,
  license_key: string
}

export namespace Products {
  export enum Pages {
    CATALOG = 'catalog',
    PRODUCT_DETAIL = 'product',
  }

  export interface ProductPrices {
    discount: number,
    nds_sum: number,
    actual: ProductPrice,
    sale_price?: ProductPriceField,
    old?: ProductPrice | null,
  }

  export interface CatalogProductImage {
    id: number,
    src: string,
  }

  export interface Product {
    [key: string]: unknown,
    id: number,
    product_code?: string | number,
    nomenclature: string,
    license_keys: string[],
    license_key?: string,
    prices: ProductPrices,
    residues?: string | number | null, // Доступные остатки
    category_name?: string | null,
    post_prod_residues?: string | number | null, // Доступные остатки после производства
    product_image?: string | null,
    quantity?: number | string, // количество
    // типы, которые в осноовном в каталоге
    description?: string,
    benefits?: Benefit[], // преимущества
    peculiarities?: string[], // особенности
    characteristics?: Characteristic[], // характеристики
    files?: ProductFile[],
    images?: CatalogProductImage[],
    payment_type?: PaymentMethods,
    percent_extra_charge?: number, // процент наценки
    is_software: boolean | null,
    is_allowed_electronic_shipment: boolean | null,
    is_automatic_shipment: boolean | null,
    is_required_license: boolean | null,
    related_products: Array<Product>
    // id баннера для статистики
    banner_id?: string | unknown,
    category_id: number,
    is_favorite?: boolean,
    is_available_to_add_cart: boolean,
    page?: string,
    is_allowed_by_realization: boolean,
  }

  export interface RelatedProduct {
    [key: string]: unknown,
    id: number,
    product_code: string,
    nomenclature: string,
    description: string | null,
    image_id: number | null,
    prices: ProductPrices,
  }

  export interface CatalogProductItem {
    [key: string]: unknown,
    id: number,
    category_id: number,
    category_name: string,
    product_code: string,
    nomenclature: string,
    license_keys?: string[], // добавляется на фронте
    quantity?: number | string, // добавляется на фронте
    page?: string, // добавляется на фронте
    prices: ProductPrices,
    residues: string | null,
    post_prod_residues: string | null,
    is_required_license: boolean,
    related_products: Array<CatalogProductItem>, // TODO выделить в отдельный интерфейс после отвязки всех роутов по получению списка товаров
    software_type_name: string | null,
  }

  export interface CatalogFilterItem {
    id: number,
    name: string,
  }

  export interface CategoryFilterItem extends CatalogFilterItem {
    is_software: boolean,
    is_zip: boolean,
  }

  export interface EquipmentModelFilterItem {
    category_id: number,
    items: CatalogFilterItem[]
  }

  export interface CatalogFilters {
    categories: CategoryFilterItem[],
    software_types: CatalogFilterItem[],
    equipment_models: EquipmentModelFilterItem[]
    has_realization_contract: boolean
  }

  export interface CatalogResponse extends ApiPagination {
    data: {
      items: CatalogProductItem[],
      filters: CatalogFilters
    }
  }
}

export type ProductsFilters = {
  [key: string]: unknown,
  search: string,
  in_stock: boolean,
  category: string[];
  select_type: string | null;
  software_type: string;
  models: number[],
  display_warehouse_id: number | null;
  is_favorite_only: boolean;
  is_allowed_by_realization: boolean
}

export type CatalogRequestFilters = {
  [key: string]: unknown,
  search: string,
  in_stock: boolean,
  categories: number[];
  software_type: number | null;
  equipment_models: number[],
  is_favorite_only: boolean,
  is_allowed_by_realization: boolean
}

export type CatalogRequest = {
  filters: CatalogRequestFilters,
  filters_exist: boolean,
  pagination: RequestPagination,
}

export enum CatalogView {
  TABLE = 'table',
  CARDS = 'cards',
  STORAGE_PREFIX = 'catalog_view',
}

export enum CatalogFilterType {
  ALL = 'all',
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}
export interface CategoriesSlug {
  id: number,
  is_software: boolean,
  is_zip: boolean,
  name: string
}

export interface SoftwareTypesInterface {
  id: number,
  slug: string,
  name: string
}

export interface ProductsList extends BaseResponse, ApiPagination {
  data: {
    is_new_catalog: boolean,
    items: Products.Product[],
    categories: CategoriesSlug[],
    software_types: SoftwareTypesInterface[]
  }
}

export interface ModelsList extends BaseResponse {
  data: {
    items: number[],
  }
}

export interface ProdQtyUpdateDTO {
  id: number | string,
  quantity: number
}

export interface AddProductDialog {
  [key: string]: unknown,
  visible: boolean,
  title: string,
  type: string,
}
